import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PopupRef, UIconModule, UBadgeComponent, UTooltipDirective } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { collectionContent } from '../../helpers/collection.content';
import { ExperiencesService } from 'src/app/bar/services/experiences.service';
import { addAssistantsContent } from '../../../settings/components/assistant-page/helpers/assistant.content';
import { WORKSPACE_PERMISSIONS, WorkspacePermissionService } from 'src/app/bar/services/workspace-permissions.service';
import { WorkspacesService } from 'src/app/bar/services';
import { Constants } from '@local/common';
import { Config } from '@environments/config';

export type MultiChoicePopupType = 'collections' | 'wikis' | 'wiki' | 'assistants' | 'slack' | 'chat-assistants';

export type OptionValue = {
  text: string;
  description: string;
  icon: any;
  value: string;
  disabled?: boolean;
  warningText?: string;
  badge?: { text: string; style?: string };
};

export interface MultiChoicePopupData {
  type: MultiChoicePopupType;
}

@UntilDestroy()
@Component({
  selector: 'multi-choice-popup',
  templateUrl: './multi-choice-popup.component.html',
  styleUrls: ['./multi-choice-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, UIconModule, UBadgeComponent, UTooltipDirective],
})
export class MultiChoicePopupComponent implements OnInit {
  @Output() onClickEvent = new EventEmitter<any>();
  options: OptionValue[];
  selectedIndex: number = 0;

  isSelfHosted: WritableSignal<boolean> = signal(null);

  constructor(
    private ref: PopupRef<MultiChoicePopupComponent, MultiChoicePopupData>,
    private cdr: ChangeDetectorRef,
    private assistantService: ExperiencesService,
    private workspacePermissionService: WorkspacePermissionService,
    private workspaceService: WorkspacesService
  ) {}

  ngOnInit(): void {
    this.isSelfHosted.set(Config.isSelfHosted);
    switch (this.ref.data.type) {
      case 'collections':
        this.buildCollectionsOptions();
        break;
      case 'wiki':
      case 'wikis':
        this.buildWikisOptions();
        break;
      case 'assistants': {
        this.options = addAssistantsContent;
        const showAgentic = this.workspacePermissionService.adminWorkspacePermission(WORKSPACE_PERMISSIONS.ASSISTANT_AGENTIC);
        if (!showAgentic) {
          this.options = this.options.filter((option) => option.value !== 'agentic');
        }
        this.initSlackBotInstallInfo();
        this.disableAssistantByWorksSpaces();
        break;
      }
      case 'chat-assistants': {
        const validTypes = ['general'];
        const showAgentic = this.workspacePermissionService.adminWorkspacePermission(WORKSPACE_PERMISSIONS.ASSISTANT_AGENTIC);
        if (showAgentic) {
          validTypes.push('agentic');
        }
        this.options = addAssistantsContent.filter((option) => validTypes.includes(option.value));
        this.disableAssistantByWorksSpaces();
        break;
      }
      case 'slack':
        this.buildSlackOptions();
        break;
    }
  }

  private disableAssistantByWorksSpaces() {
    this.options.forEach((option) => {
      if (option.value === 'slack' && option.disabled) {
        return;
      }
      option.disabled = !this.workspacePermissionService.hasWorkspacePermission(option.value.toLowerCase() as WORKSPACE_PERMISSIONS);
    });
  }

  private async initSlackBotInstallInfo() {
    this.assistantService.disableCreateSlackAssistant$.pipe(untilDestroyed(this)).subscribe((val) => {
      if (!val) {
        return;
      }
      this.options.forEach((option) => {
        if (option.value === 'slack') {
          option.disabled = true;
          option.warningText = val;
        }
      });
    });
  }

  private buildCollectionsOptions() {
    this.options = [
      {
        text: 'Curated Collection',
        description: collectionContent.newCuratedCollectionPopupDescription,
        icon: { type: 'font', value: 'icon-curated' },
        value: 'Static',
      },
    ];
    if (!this.workspaceService.isFeatureFlagDisabled(Constants.DISABLED_SEARCH_WORKSPACE_FEATURE_FLAG)) {
      this.options.push({
        text: 'Live Collection',
        description: collectionContent.newLiveCollectionPopupDescription,
        icon: { type: 'font', value: 'icon-live-collection' },
        value: 'Live',
      });
    }
  }

  private buildWikisOptions() {
    this.options = [
      {
        text: 'New Card',
        description: collectionContent.newCardPopupDescription,
        icon: { type: 'font', value: 'icon-card' },
        value: 'Card',
      },
    ];
    if (this.ref.data.type === 'wikis') {
      this.options.unshift({
        text: 'New Wiki',
        description: collectionContent.newWikiCollectionPopupDescription,
        icon: { type: 'font', value: 'icon-wiki' },
        value: 'Wiki',
      });
    } else {
      this.options.push({
        text: 'New Folder',
        description: collectionContent.newFolderPopupDescription,
        icon: { type: 'font', value: 'icon-folder' },
        value: 'Folder',
      });
    }
  }

  private buildSlackOptions() {
    this.options = [];

    if (!this.isSelfHosted()) {
      this.options.push({
        text: 'Unleash app',
        description: 'The full Unleash AI experience in Slack',
        icon: { type: 'img', value: { lightUrl: 'assets/appicon.svg' } },
        value: 'Unleash',
      });
    }

    this.options.push(
      {
        text: 'Custom app',
        description: 'Customize Unleash app to fit your team’s need',
        icon: { type: 'img', value: { lightUrl: 'assets/slack-bot.png' } },
        value: 'Custom',
      },
      {
        text: 'AI Agent',
        description: 'Access an Unleash Assistant in Slack',
        icon: { type: 'font', value: 'icon-assistant' },
        value: 'Agent',
      }
    );
  }

  onClickOption(option) {
    if (option.disabled) {
      return;
    }
    this.onClickEvent.emit(option.value);
    this.ref.destroy();
  }

  onHoverItem(index: number) {
    this.selectedIndex = index;
    this.cdr.markForCheck();
  }
}
